// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { get } from './api';
import { ENDPOINTS } from '../utils/constants';
import type {
  MetricSeries,
  TelemetryEnvelope,
  TelemetryMetric,
  TelemetryObservations,
  TelemetryPoint,
} from '../types';

export interface EquipmentObservationsQuery {
  /** Owning organization id (required by the telemetry API). */
  orgId: string;
  /** Window start, ISO-8601 date-time. */
  start: string;
  /** Window end, ISO-8601 date-time. */
  end: string;
  /** Optional aggregation resolution (ISO-8601 duration, e.g. PT1H). */
  resolution?: string;
  /** Optional rollup function (avg, min, max, sum, count). */
  rollup?: string;
}

/**
 * Fetch observed telemetry for a piece of equipment over a time window. Returns
 * the raw `{ data: { resource, metrics, points } }` envelope the BFF preserves.
 */
export const getEquipmentObservations = (
  siteId: string,
  equipmentId: string,
  { orgId, start, end, resolution, rollup }: EquipmentObservationsQuery
): Promise<TelemetryEnvelope> => {
  const params = new URLSearchParams({ orgId, start, end });
  if (resolution) params.set('resolution', resolution);
  if (rollup) params.set('rollup', rollup);
  const base = ENDPOINTS.TELEMETRY_EQUIPMENT_OBSERVATIONS(siteId, equipmentId);
  return get<TelemetryEnvelope>(`${base}?${params.toString()}`);
};

/**
 * Resolve a metric's value on a point. Upstream models per-metric values as
 * free-form additional properties, so try the metric's pointKey, then its name,
 * then its pointId. Returns a finite number or null when absent/non-numeric.
 */
function pointValue(point: TelemetryPoint, metric: TelemetryMetric): number | null {
  const raw = point[metric.pointKey] ?? point[metric.name] ?? point[metric.pointId];
  if (raw === null || raw === undefined || raw === '') return null;
  const num = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(num) ? num : null;
}

/**
 * Turn the observations payload into one chartable series per metric. Each series
 * carries Carbon-tabular `{ group, key, value }` rows (group = metric label,
 * key = ISO timestamp). Metrics that yield no numeric points are dropped so the
 * caller can render nothing when there is effectively no telemetry.
 */
export function toMetricSeries(
  observations: TelemetryObservations | null | undefined
): MetricSeries[] {
  const metrics = observations?.metrics ?? [];
  const points = observations?.points ?? [];
  const series: MetricSeries[] = [];

  for (const metric of metrics) {
    const group = metric.unit ? `${metric.name} (${metric.unit})` : metric.name;
    const data: MetricSeries['data'] = [];
    for (const point of points) {
      const value = pointValue(point, metric);
      if (value === null) continue;
      data.push({ group, key: point.timestamp, value });
    }
    if (data.length > 0) {
      series.push({ name: metric.name, unit: metric.unit, data });
    }
  }

  return series;
}
